import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { adjustStock } from "../services/productService";
import { labelFor } from "../services/enumLabels";

/** Types that add stock; the rest take it away. Mirrors StockMovement::INCOMING. */
const INCOMING = ["in", "return"];
const TYPES = ["in", "out", "adjustment", "sale", "return"];

const EMPTY = { type: "in", quantity: "", reason: "", reference: "" };

/**
 * Records one stock movement. `adjustment` is the only type that carries its own
 * sign — a stock count can correct in either direction — so its input allows a
 * negative number while the others take a plain magnitude.
 */
export default function StockAdjustModal({ branchId, product, open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setValues(EMPTY);
      setErrors({});
      setBanner("");
    }
  }, [open, product]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, saving]);

  const current = product?.stock_quantity ?? 0;
  const signed = values.type === "adjustment";

  /** What the branch would be left with — shown before the request is sent. */
  const projected = useMemo(() => {
    const amount = Number(values.quantity);
    if (values.quantity === "" || Number.isNaN(amount)) return null;

    const delta = signed
      ? amount
      : INCOMING.includes(values.type)
        ? Math.abs(amount)
        : -Math.abs(amount);

    return current + delta;
  }, [values.quantity, values.type, signed, current]);

  if (!open) return null;

  const fieldError = (name) => errors[name]?.[0];
  const wouldGoNegative = projected !== null && projected < 0;

  const submit = async (e) => {
    e.preventDefault();
    setBanner("");

    const amount = Number(values.quantity);
    const clientErrors = {};

    if (values.quantity === "" || !Number.isInteger(amount) || amount === 0) {
      clientErrors.quantity = [t("products.stock.errors.quantity")];
    } else if (wouldGoNegative) {
      clientErrors.quantity = [t("products.stock.errors.negative", { current })];
    }

    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      const { product: saved, movement } = await adjustStock(branchId, product.id, values);
      onSaved(saved, movement);
      onClose();
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("products.stock.failed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} noValidate>
          <div className="flex items-center justify-between border-b border-line px-5 py-3">
            <h2 className="text-sm font-semibold">{t("products.stock.title")}</h2>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              aria-label={t("common.close")}
              className="rounded p-1 text-ink-muted hover:bg-raised hover:text-ink-2"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            {banner && (
              <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {banner}
              </div>
            )}

            <div className="rounded-lg bg-raised px-3 py-2">
              <p className="text-sm font-medium">{product?.name}</p>
              <p className="text-xs text-ink-2">
                {t("products.stock.current", { count: current })}
              </p>
            </div>

            <fieldset disabled={saving} className="space-y-4">
              <div>
                <label className="field-label" htmlFor="movement-type">
                  {t("products.stock.type")} <span className="text-danger-text">*</span>
                </label>
                <select
                  id="movement-type"
                  className="field-input"
                  value={values.type}
                  onChange={(e) => setValues((v) => ({ ...v, type: e.target.value }))}
                >
                  {TYPES.map((type) => (
                    <option key={type} value={type}>
                      {labelFor("stock_movement", type)}
                    </option>
                  ))}
                </select>
                {fieldError("type") && <p className="field-error">{fieldError("type")}</p>}
              </div>

              <div>
                <label className="field-label" htmlFor="movement-quantity">
                  {t("products.stock.quantity")} <span className="text-danger-text">*</span>
                </label>
                <input
                  id="movement-quantity"
                  type="number"
                  step="1"
                  min={signed ? undefined : "1"}
                  dir="ltr"
                  className="field-input"
                  value={values.quantity}
                  onChange={(e) => setValues((v) => ({ ...v, quantity: e.target.value }))}
                />
                <p className="mt-1 text-xs text-ink-2">
                  {signed ? t("products.stock.signedHint") : t("products.stock.magnitudeHint")}
                </p>
                {fieldError("quantity") && <p className="field-error">{fieldError("quantity")}</p>}
              </div>

              {projected !== null && (
                <p
                  className={`rounded-lg px-3 py-2 text-sm ${
                    wouldGoNegative ? "bg-danger-wash text-danger-text" : "bg-accent-wash text-accent-text"
                  }`}
                >
                  {t("products.stock.projected", { count: projected })}
                </p>
              )}

              <div>
                <label className="field-label" htmlFor="movement-reason">
                  {t("products.stock.reason")}
                </label>
                <input
                  id="movement-reason"
                  className="field-input"
                  placeholder={t("products.stock.reasonPlaceholder")}
                  value={values.reason}
                  onChange={(e) => setValues((v) => ({ ...v, reason: e.target.value }))}
                />
                {fieldError("reason") && <p className="field-error">{fieldError("reason")}</p>}
              </div>

              <div>
                <label className="field-label" htmlFor="movement-reference">
                  {t("products.stock.reference")}
                </label>
                <input
                  id="movement-reference"
                  dir="ltr"
                  className="field-input"
                  placeholder="PO-1042"
                  value={values.reference}
                  onChange={(e) => setValues((v) => ({ ...v, reference: e.target.value }))}
                />
                {fieldError("reference") && <p className="field-error">{fieldError("reference")}</p>}
              </div>
            </fieldset>
          </div>

          <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
            <button type="button" onClick={onClose} disabled={saving} className="btn-secondary">
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={saving || wouldGoNegative}
              className="btn-primary"
            >
              {saving ? t("products.form.saving") : t("products.stock.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
